import React, { useRef, useCallback } from 'react';
import { MessageInput, MessageInputProps } from 'stream-chat-react';
import { useChatAutocomplete } from '../celebration-chat/hooks/useChatAutocomplete';
import type { AutocompleteItem } from '../celebration-chat/hooks/useChatAutocomplete';
import { AutocompleteDropdown } from '../celebration-chat/AutocompleteDropdown';

export const CustomMessageInput: React.FC<MessageInputProps> = (props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSnapshotRef = useRef<{ value: string; cursor: number }>({ value: '', cursor: -1 });

  const {
    state,
    handleInputChange,
    moveSelection,
    getSelectedItem,
    applySelection,
    close,
  } = useChatAutocomplete({
    extraCommands: [
      { name: 'message', description: 'Start a direct message (/message @Name)', icon: '💬' },
      { name: 'Broadcast', description: 'Send a broadcast message', icon: '📢' },
    ],
  });

  const handleSelect = useCallback((item: AutocompleteItem) => {
    const textarea = containerRef.current?.querySelector('textarea');
    if (!textarea) return;

    const { newText, newCursorPos } = applySelection(textarea.value, textarea.selectionStart, item);
    lastSnapshotRef.current = { value: newText, cursor: newCursorPos };
    
    // Update textarea value using native setter to trigger React's onChange
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
    nativeInputValueSetter?.call(textarea, newText);
    
    // Dispatch input event so stream-chat-react picks up the change
    const event = new Event('input', { bubbles: true });
    textarea.dispatchEvent(event);
    
    // Set cursor position
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    close();
    textarea.focus();
  }, [applySelection, close]);

  // Use capture phase for keydown to intercept before stream-chat-react
  const handleKeyDownCapture = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!state.isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      moveSelection('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      moveSelection('up');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const item = getSelectedItem();
      if (item) {
        handleSelect(item);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }, [state.isOpen, moveSelection, getSelectedItem, close, handleSelect]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLTextAreaElement;
    if (target.tagName === 'TEXTAREA') {
      const value = target.value;
      const cursor = target.selectionStart ?? value.length;
      const last = lastSnapshotRef.current;
      if (last.value === value && last.cursor === cursor) {
        return;
      }
      lastSnapshotRef.current = { value, cursor };
      handleInputChange(value, cursor);
    }
  }, [handleInputChange]);

  const handleSelectEvent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLTextAreaElement;
    if (target.tagName === 'TEXTAREA') {
      const value = target.value;
      const cursor = target.selectionStart ?? value.length;
      const last = lastSnapshotRef.current;
      if (last.value === value && last.cursor === cursor) {
        return;
      }
      lastSnapshotRef.current = { value, cursor };
      handleInputChange(value, cursor);
    }
  }, [handleInputChange]);

  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      const target = e.target as HTMLTextAreaElement;
      if (target.tagName === 'TEXTAREA') {
        const value = target.value;
        const cursor = target.selectionStart ?? value.length;
        const last = lastSnapshotRef.current;
        if (last.value === value && last.cursor === cursor) {
          return;
        }
        lastSnapshotRef.current = { value, cursor };
        handleInputChange(value, cursor);
      }
    }
  }, [handleInputChange]);

  return (
    <div 
      ref={containerRef} 
      className="relative w-full"
      onKeyDownCapture={handleKeyDownCapture}
      onInput={handleInput}
      onClick={handleSelectEvent}
      onKeyUp={handleKeyUp}
    >
      <AutocompleteDropdown 
        state={state} 
        onSelect={handleSelect} 
        onClose={close} 
      />
      <MessageInput {...props} />
    </div>
  );
};
